<template>
	<v-container>
		<v-breadcrumbs :items="breadcrumbs" class="px-0 mb-4">
			<template v-slot:divider>
				<v-icon>mdi-chevron-right</v-icon>
			</template>
		</v-breadcrumbs>

		<div id="readSupply">
			<v-row>
				<v-col cols="12">
					<v-card :loading="loading">
						<div class="ma-4" v-if="loading && !supply">Cargando...</div>

						<v-card-text v-if="supply">
							<!-- Nombre + acción -->
							<div class="d-flex align-start justify-space-between flex-wrap gap-2 mb-1">
								<div>
									<div class="caption text--secondary mb-1">Abastecimiento</div>
									<div class="text-h5">{{ supply.name }}</div>
								</div>
								<v-btn
									color="primary"
									class="text-none mt-1"
									outlined
									small
									@click="openEditDialog"
								>
									<v-icon left small>mdi-pencil</v-icon>
									Editar
								</v-btn>
							</div>

							<div class="mb-4"></div>

							<!-- Proveedor / cliente — una sola vez -->
							<div class="d-flex align-center flex-wrap mb-1">
								<v-icon small left color="primary">mdi-account-circle-outline</v-icon>
								<span class="font-weight-medium mr-2">{{ supply.owner.name }}</span>
								<v-chip x-small outlined>
									{{ supply.owner.type === 'PROVIDER' ? 'Proveedor' : 'Cliente' }}
								</v-chip>
							</div>
							<div class="caption text--secondary mb-4">
								RUC / Doc.: <span class="font-weight-medium">{{ supply.owner.document }}</span>
							</div>

							<v-divider class="mb-4" />

							<!-- Fechas -->
							<div class="caption text-uppercase text--secondary font-weight-bold mb-2">
								Vigencia y registro
							</div>

							<v-row justify="space-between" class="my-1" dense>
								<v-col cols="auto" class="text--secondary">Inicio de vigencia</v-col>
								<v-col cols="auto">{{ formatDateOnly(supply.start_date) }}</v-col>
							</v-row>
							<v-row justify="space-between" class="my-1" dense>
								<v-col cols="auto" class="text--secondary">Fin de vigencia</v-col>
								<v-col cols="auto">{{ formatDateOnly(supply.end_date) }}</v-col>
							</v-row>
							<v-row justify="space-between" class="my-1" dense>
								<v-col cols="auto" class="text--secondary">Fecha de registro</v-col>
								<v-col cols="auto">{{ formatDate(supply.created_at) }}</v-col>
							</v-row>

							<!-- Barra de progreso de vigencia -->
							<div v-if="supply.start_date && supply.end_date" class="mt-5 mb-1">
								<div class="d-flex justify-space-between align-center mb-2">
									<div>
										<div class="caption text--disabled" style="line-height:1.2">Inicio</div>
										<div class="caption font-weight-medium">{{ formatDateOnly(supply.start_date) }}</div>
									</div>
									<span class="caption font-weight-bold" :class="`${vigenciaColor}--text`">
										{{ vigenciaProgress }}% transcurrido
									</span>
									<div class="text-right">
										<div class="caption text--disabled" style="line-height:1.2">Fin</div>
										<div class="caption font-weight-medium">{{ formatDateOnly(supply.end_date) }}</div>
									</div>
								</div>
								<v-progress-linear
									:value="vigenciaProgress"
									:color="vigenciaColor"
									rounded
									height="8"
									background-color="grey lighten-3"
								/>
								<div class="d-flex justify-space-between align-center mt-2">
									<span class="caption text--secondary">
										<v-icon x-small class="mr-1" :color="vigenciaColor">mdi-calendar-today</v-icon>
										Hoy: <span class="font-weight-medium">{{ vigenciaHoy }}</span>
									</span>
									<span
										class="caption font-weight-medium"
										:class="vigenciaDaysRemaining < 0 ? 'error--text' : `${vigenciaColor}--text`"
									>
										{{ vigenciaLabel }}
									</span>
								</div>
							</div>

							<v-sheet class="my-6" outlined rounded>
								<v-row class="px-4 py-2" align="center" dense>
									<v-col cols="auto">
										<div class="font-weight-bold text--secondary">Contratos y adendas:</div>
									</v-col>
									<v-spacer></v-spacer>
									<v-col cols="auto">
										<v-btn color="primary" small @click="dialog = true">
											Agregar
											<v-icon right light>mdi-plus</v-icon>
										</v-btn>
									</v-col>
								</v-row>

								<v-divider class="mb-4"></v-divider>

								<v-data-table
									:headers="documentHeaders"
									:items="supply.documents || []"
									:items-per-page="-1"
									:loading="loadingDocuments"
									hide-default-footer
									dense
								>
									<template v-slot:[`item.name`]="{ item }">
										<span>{{ item.name }}</span>
									</template>

									<template v-slot:[`item.type`]="{ item }">
										<v-chip
											v-if="item.type"
											x-small
											:color="item.type === 'CONTRATO' ? 'blue darken-1' : 'orange darken-1'"
											dark
											label
											class="font-weight-medium"
										>
											{{ item.type }}
										</v-chip>
										<span v-else class="grey--text caption">—</span>
									</template>

									<template v-slot:[`item.created_at`]="{ item }">
										<span class="text-no-wrap">{{ formatDate(item.created_at) }}</span>
									</template>

									<template v-slot:[`item.actions`]="{ item }">
										<div class="d-flex align-center justify-end" style="white-space: nowrap">
											<v-btn
												color="primary"
												small
												icon
												:loading="readingFile === item.id"
												@click="openFile(item)"
												v-if="item.file_url"
												title="Vista previa"
											>
												<v-icon small>mdi-eye-outline</v-icon>
											</v-btn>
											<v-btn
												color="primary"
												small
												icon
												:href="item.file_url"
												:download="item.name"
												target="_blank"
												rel="noopener noreferrer"
												v-if="item.file_url"
												title="Descargar"
											>
												<v-icon small>mdi-download</v-icon>
											</v-btn>
											<v-btn
												color="primary"
												small
												icon
												@click="openEditDocument(item)"
												title="Editar"
											>
												<v-icon small>mdi-pencil-outline</v-icon>
											</v-btn>
											<v-btn
												color="error"
												small
												icon
												:loading="deletingFile === item.id"
												@click="confirmDelete(item)"
												title="Eliminar"
											>
												<v-icon small>mdi-trash-can-outline</v-icon>
											</v-btn>
										</div>
									</template>
								</v-data-table>

								<div
									v-if="!supply.documents || supply.documents.length === 0"
									class="text-center mb-6 text--disabled"
								>
									No hay datos disponibles
								</div>
							</v-sheet>
						</v-card-text>

						<v-card-text v-if="!loading && !supply">
							<div class="text-body-1 text--secondary">
								Registro no encontrado.
							</div>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
		</div>

		<!-- Dialog editar abastecimiento -->
		<v-dialog v-model="editDialog" width="600" persistent>
			<v-card>
				<v-card-title class="text-h6">
					Editar abastecimiento
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-5">
					<v-form ref="editForm" v-model="editValid" lazy-validation>
						<v-row dense>
							<v-col cols="12">
								<v-text-field
									v-model="editForm.name"
									label="Nombre del contrato"
									outlined
									dense
									:rules="requiredRule"
								/>
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="editForm.start_date"
									type="date"
									label="Fecha de inicio"
									outlined
									dense
									:rules="startDateRules"
								/>
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="editForm.end_date"
									type="date"
									label="Fecha de término"
									outlined
									dense
									:rules="endDateRules"
								/>
							</v-col>
							<v-col cols="12">
								<v-autocomplete
									v-model="editForm.owner"
									:items="owners"
									item-value="id"
									item-text="name"
									label="Proveedor"
									placeholder="Búsqueda de cliente/proveedor"
									outlined
									dense
									no-filter
									return-object
									:loading="ownerLoading"
									:rules="requiredRule"
									@update:search-input="searchOwner"
								/>
							</v-col>
							<v-col cols="12">
								<v-btn color="primary" class="text-none" text small @click="createOwnerDialog = true">
									<v-icon left small>mdi-account-plus</v-icon>
									Agregar cliente/proveedor
								</v-btn>
							</v-col>
						</v-row>
					</v-form>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="closeEditDialog">Cancelar</v-btn>
					<v-btn color="primary" text :loading="saving" @click="saveEdit">
						Guardar cambios
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog editar documento -->
		<v-dialog v-model="editDocumentDialog" width="440" persistent>
			<v-card>
				<v-card-title class="text-h6">Editar documento</v-card-title>
				<v-divider />
				<v-card-text class="pt-5">
					<v-form ref="editDocumentForm" v-model="editDocumentValid" lazy-validation>
						<v-text-field
							v-model="editDocumentForm.name"
							label="Nombre del documento"
							outlined
							dense
							:rules="requiredRule"
							class="mb-4"
						/>

						<div class="caption mb-2 font-weight-medium">
							Tipo de documento <span class="error--text">*</span>
						</div>
						<div :class="['type-toggle-wrapper', { 'type-toggle-wrapper--error': editTypeError }]">
							<v-btn-toggle
								v-model="editDocumentForm.type"
								color="primary"
								dense
								class="annex-type-toggle"
								@change="editTypeError = false"
							>
								<v-btn value="CONTRATO" small class="text-none">
									<v-icon left x-small>mdi-file-document-outline</v-icon>
									Contrato
								</v-btn>
								<v-btn value="ADENDA" small class="text-none">
									<v-icon left x-small>mdi-file-plus-outline</v-icon>
									Adenda
								</v-btn>
							</v-btn-toggle>
						</div>
						<div v-if="editTypeError" class="caption error--text mt-1">
							Debe seleccionar el tipo de documento
						</div>
					</v-form>
				</v-card-text>
				<v-divider />
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="closeEditDocument">Cancelar</v-btn>
					<v-btn color="primary" text :loading="savingDocument" @click="saveEditDocument">
						Guardar cambios
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<create-owner-dialog v-model="createOwnerDialog" fixed-type="PROVIDER" />

		<!-- Dialog agregar contrato / adenda -->
		<v-dialog v-model="dialog" width="640" persistent>
			<v-card>
				<v-card-title class="text-h6">Agregar contrato o adenda</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-5">
					<v-form ref="documentForm" v-model="validForm" lazy-validation>
						<v-text-field
							v-model="documentForm.name"
							outlined
							dense
							label="Nombre del documento"
							:rules="requiredRule"
						></v-text-field>

						<div class="mb-5">
							<div class="caption mb-2 font-weight-medium">
								Tipo de documento <span class="error--text">*</span>
							</div>
							<div :class="['type-toggle-wrapper', { 'type-toggle-wrapper--error': typeError }]">
								<v-btn-toggle
									v-model="documentForm.type"
									color="primary"
									dense
									class="annex-type-toggle"
									@change="typeError = false"
								>
									<v-btn value="CONTRATO" small class="text-none">
										<v-icon left x-small>mdi-file-document-outline</v-icon>
										Contrato
									</v-btn>
									<v-btn value="ADENDA" small class="text-none">
										<v-icon left x-small>mdi-file-plus-outline</v-icon>
										Adenda
									</v-btn>
								</v-btn-toggle>
							</div>
							<div v-if="typeError" class="caption error--text mt-1">
								Debe seleccionar el tipo de documento
							</div>
						</div>

						<div>
							<div class="caption mb-2 font-weight-medium">
								Archivo adjunto <span class="error--text">*</span>
							</div>
							<input
								ref="fileInput"
								type="file"
								accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif"
								style="display: none"
								@change="onFileChange"
							/>
							<div
								class="drop-zone"
								:class="{
									'drop-zone--active': isDragging,
									'drop-zone--has-file': !!documentForm.file,
									'drop-zone--error': !!fileError,
								}"
								@dragover.prevent="onDragOver"
								@dragleave="onDragLeave"
								@drop.prevent="onDrop"
								@click="openFilePicker"
							>
								<div v-if="!documentForm.file" class="text-center">
									<v-icon
										:color="isDragging ? 'primary' : fileError ? 'error' : 'grey lighten-1'"
										size="36"
									>
										mdi-cloud-upload-outline
									</v-icon>
									<div class="caption mt-1 grey--text text--darken-1">
										Arrastra tu archivo aquí o
										<span class="primary--text font-weight-medium">haz clic para seleccionar</span>
									</div>
									<div class="caption grey--text">PDF, PNG, JPG/JPEG, TIFF</div>
								</div>
								<div
									v-else
									class="d-flex align-center justify-space-between px-1"
									style="width: 100%"
								>
									<div class="d-flex align-center overflow-hidden">
										<v-icon color="primary" class="mr-2 flex-shrink-0">mdi-file-check-outline</v-icon>
										<span class="caption primary--text font-weight-medium text-truncate">
											{{ documentForm.file.name }}
										</span>
									</div>
									<v-btn icon x-small class="ml-2 flex-shrink-0" @click.stop="clearFile">
										<v-icon small>mdi-close</v-icon>
									</v-btn>
								</div>
							</div>
							<div v-if="fileError" class="caption error--text mt-1">{{ fileError }}</div>
						</div>
					</v-form>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="red" text @click="closeDialog">Cerrar</v-btn>
					<v-btn color="primary" text :loading="loadingForm" @click="saveDocument">
						Guardar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatDate, formatDateOnly, showSnack } from '@/helpers/globalHelpers';
import CreateOwnerDialog from '@/components/contract/create/elements/CreateOwnerDialog/CreateOwnerDialog.vue';

export default {
	name: 'ReadSupplyView',

	data() {
		return {
			breadcrumbs: [
				{
					text: 'Inicio',
					link: true,
					exact: true,
					disabled: false,
					to: { name: 'Dashboard' },
				},
				{
					text: 'Abastecimiento',
					link: true,
					exact: true,
					disabled: false,
					to: { name: 'AgreementHome' },
				},
				{
					text: 'Documento',
					disabled: true,
					href: '/',
				},
			],
			documentHeaders: [
				{ text: 'Nombre', value: 'name', sortable: false },
				{ text: 'Tipo', value: 'type', sortable: false, width: '130px' },
				{ text: 'Fecha de Carga', value: 'created_at', sortable: false, align: 'center', width: '180px' },
				{ text: 'Funciones', value: 'actions', sortable: false, align: 'right', width: '160px' },
			],

			// edit dialog
			editDialog: false,
			createOwnerDialog: false,
			editValid: true,
			saving: false,
			ownerLoading: false,
			ownerTimeout: null,
			editForm: {
				name: null,
				start_date: null,
				end_date: null,
				owner: null,
			},

			// edit document dialog
			editDocumentDialog: false,
			editDocumentValid: true,
			editTypeError: false,
			savingDocument: false,
			editingDocumentId: null,
			editDocumentForm: { name: null, type: null },

			// add document dialog
			dialog: false,
			validForm: true,
			loadingForm: false,
			loadingDocuments: false,
			typeError: false,
			fileError: null,
			isDragging: false,
			readingFile: null,
			deletingFile: null,

			requiredRule: [(v) => !!v || 'Campo requerido'],
			documentForm: {
				name: null,
				type: null,
				file: null,
			},
		};
	},

	computed: {
		...mapState('supply', ['supply', 'loading']),
		...mapState('owner', ['owners']),

		supplyId() {
			return this.$route.params?.agreementId || null;
		},

		startDateRules() {
			return [
				(v) => !!v || 'Campo requerido',
				(v) =>
					!this.editForm.end_date ||
					v < this.editForm.end_date ||
					'Debe ser menor a la fecha de término',
			];
		},

		endDateRules() {
			return [
				(v) => !!v || 'Campo requerido',
				(v) =>
					!this.editForm.start_date ||
					v > this.editForm.start_date ||
					'Debe ser mayor a la fecha de inicio',
			];
		},

		vigenciaProgress() {
			if (!this.supply?.start_date || !this.supply?.end_date) return 0;
			const startStr = String(this.supply.start_date).slice(0, 10);
			const endStr = String(this.supply.end_date).slice(0, 10);
			const todayStr = new Date().toISOString().slice(0, 10);
			if (todayStr <= startStr) return 0;
			if (todayStr >= endStr) return 100;
			const start = new Date(startStr).getTime();
			const end = new Date(endStr).getTime();
			const now = new Date(todayStr).getTime();
			return Math.round(((now - start) / (end - start)) * 100);
		},


		vigenciaColor() {
			const p = this.vigenciaProgress;
			if (p >= 90) return 'error';
			if (p >= 70) return 'warning';
			return 'success';
		},

		vigenciaDaysRemaining() {
			if (!this.supply?.end_date) return null;
			const endStr = String(this.supply.end_date).slice(0, 10);
			const todayStr = new Date().toISOString().slice(0, 10);
			const end = new Date(endStr).getTime();
			const today = new Date(todayStr).getTime();
			return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
		},


		vigenciaHoy() {
			return this.formatDate(new Date().toISOString());
		},

		vigenciaLabel() {
			const d = this.vigenciaDaysRemaining;
			if (d === null) return '';
			if (d < 0) return `Vencido hace ${Math.abs(d)} días`;
			if (d === 0) return 'Vence hoy';
			return `Quedan ${d} días`;
		},
	},

	created() {
		this.fetchSupply();
	},

	methods: {
		...mapActions('supply', ['getSupply', 'updateSupply', 'createSupplyDocument', 'updateSupplyDocument', 'deleteSupplyDocument']),
		...mapActions('owner', ['listOwners']),
		...mapActions('contract', ['signedFileUrl']),
		formatDate,
		formatDateOnly,

		async fetchSupply() {
			if (!this.supplyId) return;
			const { ok, error } = await this.getSupply(this.supplyId);
			if (!ok) showSnack(error?.data?.message, 'error');
		},

		// ── Edit dialog ──────────────────────────────────────────
		async openEditDialog() {
			this.editForm = {
				name: this.supply.name,
				start_date: this.supply.start_date?.slice(0, 10) || null,
				end_date: this.supply.end_date?.slice(0, 10) || null,
				owner: this.supply.owner || null,
			};
			this.editDialog = true;
			this.ownerLoading = true;
			await this.listOwners({ q: this.supply.owner?.name || '', type: 'PROVIDER' });
			this.ownerLoading = false;
		},

		closeEditDialog() {
			this.editDialog = false;
			this.$nextTick(() => {
				if (this.$refs.editForm) this.$refs.editForm.resetValidation();
			});
		},

		searchOwner(value) {
			if (value === this.editForm.owner?.name) return;
			clearTimeout(this.ownerTimeout);
			if ((value || '').length > 1 || value === '') {
				this.ownerTimeout = setTimeout(async () => {
					this.ownerLoading = true;
					await this.listOwners({ q: value, type: 'PROVIDER' });
					this.ownerLoading = false;
				}, 800);
			}
		},

		async saveEdit() {
			if (!this.$refs.editForm.validate()) return;
			this.saving = true;
			const { ok, error } = await this.updateSupply({
				id: this.supplyId,
				payload: {
					name: this.editForm.name,
					start_date: this.editForm.start_date,
					end_date: this.editForm.end_date,
					owner_id: this.editForm.owner?.id,
				},
			});
			this.saving = false;

			if (ok) {
				showSnack('Abastecimiento actualizado exitosamente', 'success');
				this.closeEditDialog();
				await this.fetchSupply();
			} else {
				const msg = error?.data?.message;
				showSnack(Array.isArray(msg) ? msg.join('\n') : msg || 'Error al actualizar', 'error');
			}
		},

		// ── Edit document dialog ─────────────────────────────────
		openEditDocument(item) {
			this.editingDocumentId = item.id;
			this.editDocumentForm = { name: item.name, type: item.type };
			this.editTypeError = false;
			this.editDocumentDialog = true;
		},

		closeEditDocument() {
			this.editDocumentDialog = false;
			this.$nextTick(() => {
				if (this.$refs.editDocumentForm) this.$refs.editDocumentForm.resetValidation();
				this.editingDocumentId = null;
				this.editDocumentForm = { name: null, type: null };
			});
		},

		async saveEditDocument() {
			const isValid = this.$refs.editDocumentForm.validate();
			this.editTypeError = !this.editDocumentForm.type;
			if (!isValid || this.editTypeError) return;

			this.savingDocument = true;
			const { ok, error } = await this.updateSupplyDocument({
				id: this.editingDocumentId,
				payload: {
					name: this.editDocumentForm.name,
					type: this.editDocumentForm.type,
				},
			});
			this.savingDocument = false;

			if (ok) {
				showSnack('Documento actualizado correctamente', 'success');
				this.closeEditDocument();
				await this.fetchSupply();
			} else {
				const msg = error?.data?.message;
				showSnack(Array.isArray(msg) ? msg.join('\n') : msg || 'Error al actualizar', 'error');
			}
		},

		// ── Add document dialog ──────────────────────────────────
		closeDialog() {
			this.dialog = false;
			this.typeError = false;
			this.fileError = null;
			this.isDragging = false;
			this.$nextTick(() => {
				if (this.$refs.documentForm) this.$refs.documentForm.reset();
				if (this.$refs.fileInput) this.$refs.fileInput.value = '';
				this.documentForm = { name: null, type: null, file: null };
			});
		},

		openFilePicker() {
			this.$refs.fileInput.click();
		},

		onFileChange(event) {
			const file = event.target.files[0];
			if (!file) return;
			if (this.isValidFile(file)) {
				this.documentForm.file = file;
				this.fileError = null;
			} else {
				this.fileError = 'Solo se permiten archivos PDF, PNG, JPG/JPEG y TIFF';
			}
			event.target.value = '';
		},

		onDragOver() {
			this.isDragging = true;
		},

		onDragLeave() {
			this.isDragging = false;
		},

		onDrop(event) {
			this.isDragging = false;
			const file = event.dataTransfer.files[0];
			if (!file) return;
			if (this.isValidFile(file)) {
				this.documentForm.file = file;
				this.fileError = null;
			} else {
				this.fileError = 'Solo se permiten archivos PDF, PNG, JPG/JPEG y TIFF';
			}
		},

		clearFile() {
			this.documentForm.file = null;
			this.fileError = null;
			if (this.$refs.fileInput) this.$refs.fileInput.value = '';
		},

		isValidFile(file) {
			const validTypes = [
				'application/pdf',
				'image/png',
				'image/jpeg',
				'image/tiff',
			];
			return validTypes.includes(file.type);
		},

		async saveDocument() {
			const isValid = this.$refs.documentForm.validate();
			this.typeError = !this.documentForm.type;

			if (!this.documentForm.file) {
				this.fileError = 'Campo requerido';
			} else if (!this.isValidFile(this.documentForm.file)) {
				this.fileError = 'Solo se permiten archivos PDF, PNG, JPG/JPEG y TIFF';
			} else {
				this.fileError = null;
			}

			if (!isValid || this.typeError || this.fileError) return;

			this.loadingForm = true;
			try {
				const formData = new FormData();
				formData.append('supply_id', this.supplyId);
				formData.append('name', this.documentForm.name);
				formData.append('type', this.documentForm.type);
				formData.append('file', this.documentForm.file, this.documentForm.file.name);

				const { ok, error } = await this.createSupplyDocument(formData);
				if (!ok) {
					showSnack(error?.data?.message || 'Error al guardar el documento', 'error');
					return;
				}

				await this.fetchSupply();
				this.closeDialog();
				showSnack('Documento agregado correctamente', 'success');
			} catch (error) {
				showSnack(error?.response?.data?.message, 'error');
			} finally {
				this.loadingForm = false;
			}
		},

		async confirmDelete(item) {
			const { isConfirmed } = await this.$swal.fire({
				title: null,
				text: '¿Estás seguro que deseas eliminar el documento?',
				icon: 'question',
				showCancelButton: true,
				cancelButtonColor: '#0039a6',
				confirmButtonColor: '#e0e0e0',
				cancelButtonText: 'No',
				confirmButtonText: 'Sí',
				reverseButtons: true,
			});
			if (isConfirmed) this.removeDocument(item);
		},

		async removeDocument(item) {
			this.deletingFile = item.id;
			const { ok, error } = await this.deleteSupplyDocument(item.id);
			this.deletingFile = null;

			if (ok) {
				showSnack('Documento eliminado exitosamente', 'success');
				await this.fetchSupply();
			} else {
				const msg = error?.data?.message;
				showSnack(Array.isArray(msg) ? msg.join('\n') : msg || 'Error al eliminar', 'error');
			}
		},

		async openFile(item) {
			this.readingFile = item.id;
			try {
				const { error, response } = await this.signedFileUrl({ fileUrl: item.file_url });
				if (error) {
					showSnack('No se pudo abrir el archivo', 'error');
					return;
				}
				const url = this.$router.resolve({
					name: 'ReadFile',
					query: { url: response.signedUrl },
				}).href;
				window.open(url, '_blank');
			} finally {
				this.readingFile = null;
			}
		},
	},

	components: {
		CreateOwnerDialog,
	},

	beforeDestroy() {
		clearTimeout(this.ownerTimeout);
		this.$store.commit('supply/SET_SUPPLY', null);
	},
};
</script>

<style lang="scss">
.annex-type-toggle {
	border: 1px solid rgba(0, 0, 0, 0.12) !important;
	border-radius: 4px !important;
}
.type-toggle-wrapper--error .annex-type-toggle {
	border-color: var(--v-error-base) !important;
}
.drop-zone {
	border: 2px dashed rgba(0, 0, 0, 0.18);
	border-radius: 8px;
	padding: 16px 12px;
	min-height: 96px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: border-color 0.2s ease, background-color 0.2s ease;
	cursor: pointer;
	user-select: none;

	&--active {
		border-color: var(--v-primary-base);
		background-color: rgba(25, 118, 210, 0.06);
	}
	&--has-file {
		border-style: solid;
		border-color: var(--v-primary-base);
		background-color: rgba(25, 118, 210, 0.03);
	}
	&--error {
		border-style: solid;
		border-color: var(--v-error-base);
	}
}
</style>
